import { IF, IS, NO, UITextView, UIView, YES } from "uicore-ts"
import { UITooltip } from "uicore-ts/compiledScripts/UITooltip"
import { RHBadges, RHFont } from "./Utils_slop/RHDesignTokens"

export type RHNotificationBadgeVariant = "nav" | "action" | "tab"
export type RHNotificationBadgeCountKind = "unread" | "task"
export type RHNotificationBadgeTaskIndicatorStyle = "chip" | "count"

export interface RHNotificationBadgeOptions {
    attentionRequired?: boolean
    countKind?: RHNotificationBadgeCountKind
    unreadTooltip?: string
    attentionTooltip?: string
    tooltipText?: string
    taskLabel?: string
    taskIndicatorStyle?: RHNotificationBadgeTaskIndicatorStyle
    // Appends a numberless orange dot after any count/chip — used for the
    // "there is also an unread message here" marker on row action buttons.
    trailingDot?: boolean
}

type RHAttentionTextViewClass = typeof UITextView & {
    attentionIndicatorHTMLString: () => string
}

type RHAttentionTextView = UITextView & {
    _text?: string
    attentionRequired: boolean
    attentionIndicatorHTMLString: () => string
}

export class RHNotificationBadge {

    static readonly _baseFontWeights = new WeakMap<UITextView, string>()
    static readonly _unreadEmphasisStyleClassName = "RHNotificationUnreadEmphasis"

    static installAttentionIndicatorRenderer() {
        const textViewClass = UITextView as RHAttentionTextViewClass
        textViewClass.attentionIndicatorHTMLString = () => RHNotificationBadge.attentionIndicatorHTMLString()
        UIView.injectCSS(
            `.${RHNotificationBadge._unreadEmphasisStyleClassName} { ` +
                `font-weight: ${RHFont.weightBold} !important; ` +
            `}`,
            "rh-notification-unread-emphasis-style"
        )
    }

    static attentionIndicatorHTMLString(
        label: string = RHBadges.notificationTaskLabel,
        taskIndicatorStyle: RHNotificationBadgeTaskIndicatorStyle = "chip"
    ) {
        return `<span style="${
            RHNotificationBadge._badgeStyle("nav", "task", taskIndicatorStyle === "count")
        }">${label}</span>`
    }

    // Simplified-system chip labels (so call sites need only RHNotificationBadge, not the tokens).
    static get newOfferLabel(): string {
        return RHBadges.notificationTaskLabels.newOffer
    }

    static get answeredQuestionLabel(): string {
        return RHBadges.notificationTaskLabels.answeredQuestion
    }

    static procurementTaskLabel(requiresEvaluation: boolean, requiresQA: boolean): string {
        const labels = RHBadges.notificationTaskLabels
        if (requiresEvaluation && requiresQA) {
            return labels.multipleProcurementActions
        }
        if (requiresEvaluation) {
            return labels.evaluateOffers
        }
        if (requiresQA) {
            return labels.answerQuestions
        }
        return RHBadges.notificationTaskLabel
    }

    static procurementTaskCount(requiresEvaluation: boolean, requiresQA: boolean): number {
        let taskCount = 0
        if (requiresEvaluation) {
            taskCount += 1
        }
        if (requiresQA) {
            taskCount += 1
        }
        return taskCount
    }

    static procurementTaskOptions(
        requiresEvaluation: boolean,
        requiresQA: boolean
    ): Pick<RHNotificationBadgeOptions, "taskLabel" | "taskIndicatorStyle"> {
        const taskCount = RHNotificationBadge.procurementTaskCount(requiresEvaluation, requiresQA)
        if (taskCount > 1) {
            return {
                taskLabel: RHNotificationBadge._visibleCount(taskCount),
                taskIndicatorStyle: "count"
            }
        }

        return {
            taskLabel: RHNotificationBadge.procurementTaskLabel(requiresEvaluation, requiresQA),
            taskIndicatorStyle: "chip"
        }
    }

    static renderHTML(
        count: number,
        variant: RHNotificationBadgeVariant = "nav",
        attentionOrOptions: boolean | RHNotificationBadgeOptions = NO
    ): string {
        const options = RHNotificationBadge._normalizedOptions(attentionOrOptions)
        if (count <= 0) {
            return ""
        }

        const countKind = options.countKind || "unread"
        if (countKind !== "task") {
            return RHNotificationBadge._informationalCountHTMLString(count)
        }

        return `<span style="${RHNotificationBadge._badgeStyle(variant, countKind)}">${RHNotificationBadge._visibleCount(count)}</span>`
    }

    static applyToTextView(
        textView: UITextView,
        count: number,
        variant: RHNotificationBadgeVariant = "nav",
        attentionOrOptions: boolean | RHNotificationBadgeOptions = NO
    ) {
        const options = RHNotificationBadge._normalizedOptions(attentionOrOptions)
        const countKind = options.countKind || "unread"
        const attentionRequired = IS(options.attentionRequired)
        const taskLabel = options.taskLabel || RHBadges.notificationTaskLabel
        const taskIndicatorStyle = options.taskIndicatorStyle || "chip"
        const attentionTextView = textView as RHAttentionTextView
        const baseText = attentionTextView._text ?? textView.text

        textView.notificationAmount = 0
        attentionTextView.attentionIndicatorHTMLString = () =>
            RHNotificationBadge.attentionIndicatorHTMLString(taskLabel, taskIndicatorStyle)
        attentionTextView.attentionRequired = attentionRequired
        textView.textSuffix = RHNotificationBadge.renderHTML(count, variant, options)

        if (attentionRequired && !RHNotificationBadge._textViewHandlesAttentionRequired()) {
            textView.textSuffix += RHNotificationBadge.attentionIndicatorHTMLString(
                taskLabel,
                taskIndicatorStyle
            )
        }

        if (options.trailingDot) {
            textView.textSuffix += RHNotificationBadge.dotHTMLString()
        }

        textView.text = baseText
        RHNotificationBadge._applyTooltip(
            textView,
            RHNotificationBadge._tooltipText(count, countKind, options)
        )
    }

    // ── Numberless attention dot ──────────────────────────────────────────────
    // A small orange filled circle, no text. Reserved for a concrete unfinished
    // action; unread information is rendered with applyUnreadEmphasis.

    /**
     * Applies or clears the shared unread presentation. Unread content is bold;
     * read content returns to the text view's original weight. The original
     * weight is captured per view so a button whose normal title is medium does
     * not get flattened to regular when its unread state clears.
     *
     * This method also clears any old suffix marker. Call applyDot afterwards
     * when the same control independently has an unfinished action.
     */
    static applyUnreadEmphasis(textView: UITextView, unread: boolean, tooltipText: string = "") {
        let baseFontWeight = RHNotificationBadge._baseFontWeights.get(textView)
        if (!baseFontWeight) {
            baseFontWeight = textView.style.fontWeight || RHFont.weightRegular
            RHNotificationBadge._baseFontWeights.set(textView, baseFontWeight)
        }

        let tooltip = ""
        if (unread) {
            tooltip = tooltipText
        }

        const attentionTextView = textView as RHAttentionTextView
        const baseText = attentionTextView._text ?? textView.text
        textView.notificationAmount = 0
        attentionTextView.attentionRequired = NO
        textView.textSuffix = ""
        // UIButton state redraws write their normal title weight on hover,
        // focus, highlight and selection. Keep that base weight inline and let
        // an important class carry the independent unread state, so neither
        // presentation can accidentally erase the other.
        textView.configureWithObject({ style: { fontWeight: baseFontWeight } })
        textView.viewHTMLElement.classList.toggle(
            RHNotificationBadge._unreadEmphasisStyleClassName,
            unread
        )
        textView.text = baseText
        RHNotificationBadge._applyTooltip(textView, tooltip)
    }

    static dotHTMLString(): string {
        return `<span style="${RHNotificationBadge._dotStyle()}" aria-hidden="true"></span>`
    }

    static _dotStyle(): string {
        return [
            "display:inline-block",
            `width:${RHBadges.notificationDotSize}px`,
            `height:${RHBadges.notificationDotSize}px`,
            `margin-left:${RHBadges.notificationDotMarginLeft}px`,
            "border-radius:999px",
            `background:${RHBadges.notificationDotBackgroundColor.stringValue}`,
            `border:1px solid ${RHBadges.notificationDotBorderColor.stringValue}`,
            `box-shadow:${RHBadges.notificationDotShadow}`,
            "vertical-align:middle",
            `transform:${RHBadges.notificationInlineVerticalTransform}`
        ].join(";")
    }

    /**
     * Shows or clears a numberless orange needs-action dot on a text view.
     * Unread information uses applyUnreadEmphasis instead.
     */
    static applyDot(textView: UITextView, show: boolean, tooltipText: string = "") {
        const attentionTextView = textView as RHAttentionTextView
        const baseText = attentionTextView._text ?? textView.text

        let suffix = ""
        let tooltip = ""
        if (show) {
            suffix = RHNotificationBadge.dotHTMLString()
            tooltip = tooltipText
        }

        textView.notificationAmount = 0
        attentionTextView.attentionRequired = NO
        textView.textSuffix = suffix
        textView.text = baseText
        RHNotificationBadge._applyTooltip(textView, tooltip)
    }

    static configurePageLabel(label: UITextView) {
        label.configureWithObject({
            backgroundColor: RHBadges.notificationPageBackgroundColor,
            textColor: RHBadges.notificationPageTextColor,
            isSingleLine: YES,
            style: {
                fontSize: RHBadges.notificationPageFontSize,
                fontWeight: RHBadges.notificationPageFontWeight,
                lineHeight: `${RHBadges.notificationPageHeight}px`
            },
            viewHTMLElement: {
                style: {
                    boxShadow: RHBadges.notificationPageShadow
                }
            }
        })
    }

    static updatePageLabel(label: UITextView, count: number) {
        if (count > 0) {
            const newWordForCount = IF(count === 1)(() => "uus").ELSE(() => "uut")
            label.text = `${RHNotificationBadge._visibleCount(count)} ${newWordForCount}`
            label.hidden = NO
        }
        else {
            label.hidden = YES
        }
    }

    static _visibleCount(count: number): string {
        const normalizedCount = Math.max(0, Math.floor(count || 0))
        if (normalizedCount > RHBadges.notificationMaxVisibleCount) {
            return `${RHBadges.notificationMaxVisibleCount}+`
        }
        return `${normalizedCount}`
    }

    static _informationalCountHTMLString(count: number): string {
        return `<span style="${RHNotificationBadge._informationalCountStyle()}">(${RHNotificationBadge._visibleCount(count)})</span>`
    }

    static _informationalCountStyle(): string {
        return [
            "display:inline-block",
            `margin-left:${RHBadges.notificationInfoCountMarginLeft}px`,
            "color:currentColor",
            `opacity:${RHBadges.notificationInfoCountOpacity}`,
            `font-size:${RHBadges.notificationInfoCountFontSize}`,
            `font-weight:${RHBadges.notificationInfoCountFontWeight}`,
            `line-height:${RHBadges.notificationInfoCountLineHeight}`,
            "letter-spacing:0",
            "vertical-align:baseline",
            `transform:${RHBadges.notificationInfoCountVerticalTransform}`,
            "white-space:nowrap",
            "font-variant-numeric:tabular-nums"
        ].join(";")
    }

    static _badgeStyle(
        variant: RHNotificationBadgeVariant,
        countKind: RHNotificationBadgeCountKind,
        compactTask: boolean = NO
    ): string {
        let backgroundColor = RHBadges.notificationNavBackgroundColor
        let textColor = RHBadges.notificationNavTextColor
        let borderColor = RHBadges.notificationNavBorderColor
        let shadow = RHBadges.notificationInlineShadow
        let minWidth = RHBadges.notificationInlineMinWidth
        let height = RHBadges.notificationInlineHeight
        let paddingX = RHBadges.notificationInlinePaddingX

        if (countKind === "task") {
            if (compactTask) {
                backgroundColor = RHBadges.notificationTaskCountBackgroundColor
                textColor = RHBadges.notificationTaskCountTextColor
                borderColor = RHBadges.notificationTaskCountBorderColor
                shadow = RHBadges.notificationTaskCountShadow
                minWidth = RHBadges.notificationTaskCountSize
                height = RHBadges.notificationTaskCountSize
                paddingX = 0
            }
            else {
                backgroundColor = RHBadges.notificationTaskBackgroundColor
                textColor = RHBadges.notificationTaskTextColor
                borderColor = RHBadges.notificationTaskBorderColor
                shadow = RHBadges.notificationTaskShadow
            }
        }
        else {
            if (variant === "action") {
                backgroundColor = RHBadges.notificationActionBackgroundColor
                textColor = RHBadges.notificationActionTextColor
                borderColor = RHBadges.notificationActionBorderColor
                shadow = RHBadges.notificationActionShadow
            }

            if (variant === "tab") {
                backgroundColor = RHBadges.notificationTabBackgroundColor
                textColor = RHBadges.notificationTabTextColor
                borderColor = RHBadges.notificationTabBorderColor
            }
        }

        return [
            "display:inline-flex",
            "align-items:center",
            "justify-content:center",
            "box-sizing:border-box",
            `min-width:${minWidth}px`,
            `height:${height}px`,
            `padding:0 ${paddingX}px`,
            `margin-left:${RHBadges.notificationMarginLeft}px`,
            `border-radius:${RHBadges.notificationInlineBorderRadius}`,
            `background:${backgroundColor.stringValue}`,
            `color:${textColor.stringValue}`,
            `border:1px solid ${borderColor.stringValue}`,
            `font-size:${RHBadges.notificationInlineFontSize}`,
            `font-weight:${RHBadges.notificationInlineFontWeight}`,
            `line-height:${RHBadges.notificationInlineLineHeight}`,
            "letter-spacing:0",
            "vertical-align:middle",
            `transform:${RHBadges.notificationInlineVerticalTransform}`,
            `box-shadow:${shadow}`
        ].join(";")
    }

    static _normalizedOptions(
        attentionOrOptions: boolean | RHNotificationBadgeOptions
    ): RHNotificationBadgeOptions {
        if (typeof attentionOrOptions === "object") {
            return attentionOrOptions
        }

        return {
            attentionRequired: attentionOrOptions
        }
    }

    static _tooltipText(
        count: number,
        countKind: RHNotificationBadgeCountKind,
        options: RHNotificationBadgeOptions
    ): string {
        if (options.tooltipText) {
            return options.tooltipText
        }

        const lines: string[] = []

        if (count > 0) {
            if (countKind === "task") {
                lines.push(options.attentionTooltip || `${RHNotificationBadge._visibleCount(count)} tegevust ootab.`)
            }
            else {
                lines.push(options.unreadTooltip || `${RHNotificationBadge._visibleCount(count)} lugemata teavitust.`)
                if (options.attentionRequired) {
                    lines.push(options.attentionTooltip || "Tegevus ootab lõpetamist.")
                }
            }
        }
        else if (options.attentionRequired) {
            lines.push(options.attentionTooltip || "Tegevus ootab lõpetamist.")
        }

        return lines.join("\n")
    }

    static _applyTooltip(textView: UITextView, tooltipText: string) {
        if (tooltipText) {
            textView.viewHTMLElement.setAttribute("aria-label", tooltipText)
            UITooltip.sharedInstance.attach(textView, tooltipText)
            return
        }

        textView.viewHTMLElement.removeAttribute("aria-label")
        UITooltip.sharedInstance.detach(textView)
    }

    static _textViewHandlesAttentionRequired() {
        return IS(Object.getOwnPropertyDescriptor(UITextView.prototype, "attentionRequired")?.set)
    }

}
